import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { siteConfig } from "@/config";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const gaId = siteConfig.gaMeasurementId;
const gaId2 = siteConfig.gaMeasurementId2;
const gaId3 = siteConfig.gaMeasurementId3;
const pubId = siteConfig.adsensePublisherId;
const gsc = siteConfig.gscVerification;

const gaIds = [gaId, gaId2, gaId3].filter(Boolean);

const scripts: Array<Record<string, unknown>> = [];
if (gaIds.length > 0) {
  scripts.push({
    src: `https://www.googletagmanager.com/gtag/js?id=${gaIds[0]}`,
    async: true,
  });
  scripts.push({
    children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());${gaIds
      .map((id) => `gtag('config','${id}');`)
      .join("")}`,
  });
}

for (const id of [pubId, siteConfig.adsensePublisherId2]) {
  if (id && id !== "ca-pub-XXXXXXXXXXXXXXXX") {
    scripts.push({
      src: `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${id}`,
      async: true,
      crossOrigin: "anonymous",
    });
  }
}

const meta: Array<Record<string, unknown>> = [
  { charSet: "utf-8" },
  { name: "viewport", content: "width=device-width, initial-scale=1" },
  { title: `${siteConfig.siteName} — ${siteConfig.description}` },
  { name: "description", content: siteConfig.description },
  { name: "author", content: siteConfig.author },
  { property: "og:site_name", content: siteConfig.siteName },
  { property: "og:type", content: "website" },
  { property: "og:title", content: siteConfig.siteName },
  { property: "og:description", content: siteConfig.description },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:title", content: siteConfig.siteName },
  { name: "twitter:description", content: siteConfig.description },
];
if (gsc) meta.push({ name: "google-site-verification", content: gsc });

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta,
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://pagead2.googlesyndication.com" },
      { rel: "preconnect", href: "https://fundingchoicesmessages.google.com" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
      },
    ],
    scripts,
    scripts_: undefined,
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}
